"use client";

import ParagraphText from "@/components/ParagraphText";
import { TProductInfo } from "@/typings";
import { Button, Card, Grid, Rate } from "@arco-design/web-react";
import React, { useCallback, useContext, useMemo, useState } from "react";
import Image from "next/image";
import { useMeasure } from "react-use";
import { animated } from "@react-spring/web";
import { Icon } from "@/components/Icons";
import { useTrackingRecordSave } from "@/api/track/use-query";
import { LoginContext } from "@/app/LoginProvider";
import LoginModal from "@/app/(routeLayout)/login/(components)/LoginModal";
import {
  ProductCategoryType,
  Property2Type,
  ChristmasCategoryType,
  HolidayType,
  TrackingEventID,
  TrackingEventType,
  WinterCategoryType,
  SpringCategory,
  EasterCategory,
  TrackingBtnId,
} from "@/shared";
import { HolidayIcon, RouteStack, TrackingEventPageID } from "@/shared";
import "./index.scss";
import {
  ShippingIconSrc,
  TitleBg1,
  TitleBg2,
  TitleBg3,
  TitleBg4,
} from "@/cdn-resources/black5";
import ClientLink from "@/components/ClientLink";
import useAddShowcase, { EventConfig } from "@/hooks/useAddShowcase";
import EvokeSkuAction from "../EvokeSkuAction";

const { Meta } = Card;
const { Row, Col } = Grid;

const LAYOUT_COL_CARD = {
  xs: { span: 8 },
  sm: { span: 8 },
  md: { span: 6 },
  lg: { span: 6 },
  xl: { span: 6 },
  xxl: { span: 4 },
};

type CategoryType =
  | ProductCategoryType
  | ChristmasCategoryType
  | WinterCategoryType
  | SpringCategory
  | EasterCategory;

interface ProductCardProps {
  product: TProductInfo;
  type: CategoryType;
  holiday?: HolidayType;
  eventConfig?: EventConfig;
}

const Black5ProductCard: React.FC<ProductCardProps> = ({
  product,
  type,
  holiday,
  eventConfig,
}) => {
  const { mutate: fetchTrackingRecordSave } = useTrackingRecordSave();

  const { checkShowcaseAuthorize, loading, isAddedShowcase } = useAddShowcase({
    productData: product,
    eventConfig: {
      btn_id: TrackingBtnId.AddShowCase,
      ...eventConfig,
    },
  });

  const [ref, { width }] = useMeasure();

  const loginCtx = useContext(LoginContext);
  const [loginVisible, $loginVisible] = useState<boolean>(false);

  const trackPageID = useMemo(() => {
    switch (holiday) {
      case HolidayType.Christmas:
        return TrackingEventPageID.Christmas;
    }
  }, [holiday]);

  const addShowcase = useCallback(
    (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      checkShowcaseAuthorize();
    },
    [checkShowcaseAuthorize]
  );

  const titleBg = useMemo(() => {
    const discountSrc = [
      {
        imgSrc: TitleBg1,
        width: 98,
        height: 30,
      },
      {
        imgSrc: TitleBg2,
        width: 118,
        height: 30,
      },
      {
        imgSrc: TitleBg3,
        width: 118,
        height: 30,
      },
      {
        imgSrc: TitleBg4,
        width: 98,
        height: 30,
      },
    ];

    return discountSrc[product.property2 - 1];
  }, [product?.property2]);

  const holidayIcon = useMemo(() => {
    if (!holiday) return null;
    return HolidayIcon[holiday];
  }, [holiday]);

  const trackProductCard = useCallback(() => {
    if (holiday !== HolidayType.Winter) {
      fetchTrackingRecordSave({
        uid: loginCtx?.userInfo?.uid,
        event_id: TrackingEventID.ProductCard,
        event_type: TrackingEventType.Click,
        page_id: trackPageID,
        product_id: product?.product_id,
      });
    }
  }, [
    holiday,
    fetchTrackingRecordSave,
    loginCtx?.userInfo?.uid,
    trackPageID,
    product?.product_id,
  ]);

  const isEasterCategory = (type: CategoryType) => {
    return Object.values(EasterCategory).includes(type as EasterCategory);
  };
  return (
    <>
      <Col className={"h-full"} {...LAYOUT_COL_CARD}>
        {loginVisible && (
          <LoginModal
            visible={loginVisible}
            onCancel={() => $loginVisible(false)}
          />
        )}
        <div className="black5-product-container">
          {(type === ProductCategoryType.Independent ||
            type === ProductCategoryType.Discount ||
            type === ChristmasCategoryType.ChristmasDeals) && (
            <div className="product-like-box">
              <div className="product-like-content">
                <Icon.Like />
                <span> {product.property3}</span>
              </div>
            </div>
          )}
          <ClientLink
            className="flex-row w-full h-full"
            href={`/${RouteStack.Product.product}/${product.hash_id}`}
            target="_blank"
            onClick={trackProductCard}
            title={product.product_name}
            aria-label={product.product_name}
          >
            <animated.div className={"w-full h-full relative"}>
              {type === ProductCategoryType.Shipping && (
                <Image
                  src={ShippingIconSrc}
                  width={107}
                  height={21}
                  alt={product.product_name}
                  className="absolute top-[3px] left-[2px] z-[10]"
                />
              )}
              <Card
                className={"product-card"}
                bordered={false}
                hoverable={true}
                cover={
                  <div
                    ref={(_ref) => {
                      if (_ref) {
                        ref(_ref);
                      }
                    }}
                    className="shop-cover"
                    style={{ height: width }}
                  >
                    <Image
                      alt="shop-cover"
                      src={product.cover}
                      width={0}
                      height={0}
                    />
                    {(type === ProductCategoryType.Independent ||
                      type === ProductCategoryType.Discount) && (
                      <div className="discount-wrapper">
                        <div className="wrapper-bg"></div>
                        {titleBg && (
                          <Image
                            src={titleBg.imgSrc}
                            width={titleBg.width}
                            height={titleBg.height}
                            alt="discount"
                            className="z-10"
                          />
                        )}
                        {product.property2 === Property2Type.Product && (
                          <div className="discount-text">
                            {product.property1} % off
                          </div>
                        )}
                        {product.property2 === Property2Type.Brand && (
                          <div className="discount-text">
                            Up to {product.property1}% off
                          </div>
                        )}
                        {product.property2 === Property2Type.Comm && (
                          <div className="discount-text">
                            <span>{product.creator_commission_rate_str}</span>
                            <span className="commission-text">
                              +{product.property1}%
                            </span>
                            <Icon.ArrowUp />
                          </div>
                        )}
                        {product.property2 === Property2Type.YooFinds && (
                          <div className="discount-text">Black Friday</div>
                        )}
                      </div>
                    )}
                    {type === ChristmasCategoryType.ChristmasDeals && (
                      <div className="christmas-wrapper">
                        <span>
                          Save {product.est_creator_commission_amount_str}
                        </span>
                      </div>
                    )}
                    {type === SpringCategory.Spring && (
                      <div className="spring-wrapper">
                        <span>
                          Earn {product.est_creator_commission_amount_str}
                        </span>
                      </div>
                    )}

                    {isEasterCategory(type) && (
                      <div className="easter-wrapper">
                        <span>
                          Earn {product.est_creator_commission_amount_str}
                        </span>
                      </div>
                    )}
                  </div>
                }
                actions={
                  type !== ProductCategoryType.Independent
                    ? [
                        <span key="1" className="shop-rating">
                          <Rate
                            readonly
                            value={product.shop_rating}
                            count={1}
                          />
                          <span className="rate-total">
                            {product.shop_rating}
                          </span>
                          / 5
                        </span>,
                      ]
                    : []
                }
              >
                <Meta
                  avatar={
                    type !== ProductCategoryType.Independent && (
                      <div className="w-full flex items-center">
                        <Image
                          className="shop-cover-img"
                          src={product.shop_cover}
                          alt={product.shop_name}
                          width={0}
                          height={0}
                        />
                        <ParagraphText
                          className="flex-1"
                          text={product.shop_name}
                        />
                      </div>
                    )
                  }
                  title={
                    <div className="flex gap-[10px] items-center">
                      {holidayIcon && (
                        <Image
                          className="shop-cover-img"
                          src={holidayIcon.iconSrc}
                          alt=""
                          width={holidayIcon.width}
                          height={holidayIcon.height}
                        />
                      )}
                      <ParagraphText
                        text={product.product_name}
                        lineClamp={2}
                      />
                    </div>
                  }
                  description={
                    <>
                      <div className="vs-content h-[70px]">
                        <div className="right">
                          <div className="content-hull">
                            <div className="content">
                              <span className="commission-rate">
                                {product?.plan_commission_rate_str}
                              </span>
                              <span className="public-commission-label">
                                <ParagraphText text={"Open comm"} />
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="left">
                          {type === ProductCategoryType.Commission ||
                          type === ChristmasCategoryType.SuperCommission ? (
                            <div className="commission-rate">
                              <Icon.Medal />
                              {product?.creator_commission_rate_str}
                              <span className="up-text">+2%</span>
                              <Icon.ArrowUp />
                            </div>
                          ) : (
                            <div className="commission-rate">
                              <Icon.Medal />
                              {product?.creator_commission_rate_str}
                            </div>
                          )}

                          <label className="commission-label">
                            <ParagraphText text={"YooFinds comm"} />
                          </label>
                        </div>
                        <div className="vs-cover">vs</div>
                      </div>
                      {/* price */}
                      <Row className="descriptions">
                        <Col span={12} className="descriptions-item">
                          <span className="descriptions-item-label">
                            Price:
                          </span>
                          <ParagraphText
                            className="price-value"
                            text={product.promotion_lower_price_str}
                          />
                        </Col>
                        {type === ProductCategoryType.Price ? (
                          <Col span={12} className="descriptions-item last">
                            <span className="descriptions-item-label">
                              Earn:
                            </span>
                            <ParagraphText
                              className="earn-value-grey"
                              text={[product.est_creator_commission_amount_str]}
                            />
                          </Col>
                        ) : (
                          <Col span={12} className="descriptions-item last">
                            <span className="descriptions-item-label-earn">
                              Earn:
                            </span>
                            <ParagraphText
                              className="earn-value"
                              text={[product.est_creator_commission_amount_str]}
                            />
                          </Col>
                        )}

                        <Col
                          span={type === ProductCategoryType.Price ? 10 : 12}
                          className="descriptions-item"
                        >
                          <ParagraphText
                            className="price-value"
                            text={product?.influencers_count_str || "-"}
                          />
                          &nbsp;
                          <span className="descriptions-item-label">
                            <ParagraphText text="creators" />
                          </span>
                        </Col>
                        {type === ProductCategoryType.Price && (
                          <Col span={14} className="descriptions-item last">
                            <Icon.Fire className="w-4" />
                            <span className="descriptions-item-label">
                              <ParagraphText text="28d solds" />
                            </span>
                            <ParagraphText
                              className="price-value-active"
                              text={product?.day28_sales_count_str || "-"}
                            />
                          </Col>
                        )}
                        {(type === SpringCategory.New ||
                          type === EasterCategory.EasterNew) && (
                          <Col span={12} className="descriptions-item last">
                            <Icon.Hot />
                            <span className="text-[#1F283C] block ml-1">
                              New Trending
                            </span>
                          </Col>
                        )}

                        {type !== ProductCategoryType.Price &&
                          type !== SpringCategory.New &&
                          type !== EasterCategory.EasterNew && (
                            <Col span={12} className="descriptions-item last">
                              <ParagraphText
                                className="price-value"
                                text={product?.day28_sales_count_str || "-"}
                              />
                              &nbsp;
                              <span className="descriptions-item-label">
                                <ParagraphText text=" 28d solds" />
                              </span>
                            </Col>
                          )}
                      </Row>
                    </>
                  }
                />
                <div
                  className={`actions flex gap-[10px] ${type === ProductCategoryType.Independent && "mt-[10px]"}`}
                >
                  <EvokeSkuAction product={product} eventConfig={eventConfig}>
                    <Button className={"free-sample"}>
                      <ParagraphText text={"Free Sample"} />
                    </Button>
                  </EvokeSkuAction>
                  <Button
                    className={"add-show-case"}
                    loading={loading}
                    onClick={addShowcase}
                  >
                    <ParagraphText
                      text={isAddedShowcase ? "Added" : "Add"}
                      className="mx-0 my-0 flex items-center"
                    />
                  </Button>
                </div>
              </Card>
            </animated.div>
          </ClientLink>
        </div>
      </Col>
    </>
  );
};

export default Black5ProductCard;
